import React, { useState, useRef, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Mail, Phone, MapPin } from 'lucide-react';

interface ContactProps {
  title: string;
  description: string;
}

const Contact: React.FC<ContactProps> = ({ title, description }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Add simple validators
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const validatePhone = (phone: string) => {
    return /^04\d{8}$/.test(phone.replace(/\D/g, ''));
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'name':
        return value.trim() ? '' : 'Please enter your name.';
      case 'email':
        if (!value.trim()) return 'Please enter your email address.';
        if (!validateEmail(value)) return 'Please enter a valid email address.';
        return '';
      case 'phone':
        if (!value.trim()) return 'Please enter your phone number.';
        if (!validatePhone(value)) return 'Please enter a valid Australian mobile number (e.g. 0412345678).';
        return '';
      case 'service':
        return value ? '' : 'Please select a service.';
      case 'message':
        return value.trim() ? '' : 'Please enter your message.';
      default:
        return '';
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error as user types
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('idle');
    // Validate all fields
    const errors: typeof fieldErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      phone: validateField('phone', formData.phone),
      service: validateField('service', formData.service),
      message: validateField('message', formData.message),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      return;
    }
    // Optimistic UI: show success immediately
    setSubmitStatus('success');
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    setFieldErrors({ name: '', email: '', phone: '', service: '', message: '' });
    // Send request in background
    fetch('https://us-central1-ebuildandconstruct.cloudfunctions.net/sendContactEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    }).catch((error) => {
      console.error('Error submitting contact form:', error);
    });
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className={`py-20 lg:py-40 bg-white transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{title}</h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="prose max-w-none">
              <p className="text-lg text-gray-600 whitespace-pre-line">
          {description}
        </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <Phone className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Phone</p>
                  <p className="text-sm text-gray-600">
                    <a href="tel:0416758756" className="hover:text-primary-500 transition-colors" aria-label="Call Ryan - Builder">Ryan - Builder: 0416758756</a>
                  </p>
                  <p className="text-sm text-gray-600">
                    <a href="tel:0422583346" className="hover:text-primary-500 transition-colors" aria-label="Call Daniel - Landscaper">Daniel - Landscaper: 0422583346</a>
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Email</p>
                  <p className="text-sm text-gray-600">
                    <a href="mailto:Ryan@ebuildandconsult.com" className="hover:text-primary-500 transition-colors" aria-label="Email Ryan@ebuildandconsult.com">Ryan@ebuildandconsult.com</a>
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-primary-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Location</p>
                  <p className="text-sm text-gray-600">Central Coast, NSW</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form or Confirmation */}
          <div className="bg-gray-50 rounded-lg p-8 min-h-[400px] flex items-center justify-center">
            {submitStatus === 'success' ? (
              <div className="w-full flex flex-col items-center justify-center text-center">
                <svg className="w-16 h-16 text-green-500 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2l4 -4m5 2a9 9 0 1 1 -18 0a9 9 0 0 1 18 0z" /></svg>
                <h3 className="text-2xl font-bold mb-2 text-green-700">Thank you for your enquiry!</h3>
                <p className="text-lg text-gray-700 mb-4">We've received your message and will get back to you as soon as possible.<br />A confirmation email has been sent to your address.</p>
                <div className="mt-4">
                  <a href="/" className="inline-block bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-6 rounded-md transition-colors duration-200">Back to Home</a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-all duration-300 focus:scale-105"
                    required
                  />
                  {fieldErrors.name && <div className="text-red-600 text-sm mt-1">{fieldErrors.name}</div>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-all duration-300 focus:scale-105"
                    required
                  />
                  {fieldErrors.email && <div className="text-red-600 text-sm mt-1">{fieldErrors.email}</div>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-all duration-300 focus:scale-105"
                    required
                  />
                  {fieldErrors.phone && <div className="text-red-600 text-sm mt-1">{fieldErrors.phone}</div>}
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700">
                    Service Interested In
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-all duration-300 focus:scale-105"
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="new-builds">New Builds</option>
                    <option value="renovations">Renovations</option>
                    <option value="pools">Pools</option>
                    <option value="landscape">Landscape</option>
                  </select>
                  {fieldErrors.service && <div className="text-red-600 text-sm mt-1">{fieldErrors.service}</div>}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-all duration-300 focus:scale-105"
                    required
                  ></textarea>
                  {fieldErrors.message && <div className="text-red-600 text-sm mt-1">{fieldErrors.message}</div>}
                </div>

                <div>
                  <Button
                    type="submit"
                    className="w-full relative overflow-hidden text-white transition-all duration-300 hover:scale-105 active:scale-95 rounded-md font-medium py-2 px-4"
                    style={{ 
                      background: 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)', 
                      border: '1px solid #A6AA7A',
                      boxShadow: '0 2px 8px 0 rgba(166,170,122,0.08)'
                    }}
                    onMouseOver={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #8C8F63 60%, #A6AA7A 100%)')}
                    onMouseOut={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #A6AA7A 60%, #C3C6A1 100%)')}
                  >
                    <span className="absolute inset-0 pointer-events-none rounded-md" style={{background: 'linear-gradient(120deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%)', opacity: 0.7}}></span>
                    <span className="relative z-10">Send Message</span>
                  </Button>
                </div>
                {submitStatus === 'error' && (
                  <div className="text-red-600 font-semibold mt-2">There was an error sending your message. Please try again.</div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 